import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { buildAlerts, type Alert } from "../lib/alerts";
import { fetchErpStock, fetchErpSummary } from "../lib/erpApi";
import { fetchMachines } from "../lib/shopfloorApi";
import { fetchRfqs } from "../lib/rfqApi";
import { listJobs } from "../lib/jobApi";
import { isReviewStatus } from "../lib/catalogModel";
import { isE2eMockMode } from "../lib/e2eFixture";

const CLOSED = new Set(["won", "lost", "cancelled"]);

const AlertsContext = createContext<Alert[]>([]);

/** Alerts for the topbar bell — the same list on every screen. */
export function useAlerts(): Alert[] {
  return useContext(AlertsContext);
}

/**
 * Fetched once for the whole shell rather than per page, so the bell cannot
 * disagree with itself as the user moves around. Every feed is optional: a
 * module the org has not enabled simply contributes no alerts.
 */
export function AlertsProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    if (isE2eMockMode()) return;
    let cancelled = false;
    void (async () => {
      const [summary, stock, machines, rfqs, jobs] = await Promise.all([
        fetchErpSummary().catch(() => null),
        fetchErpStock().catch(() => null),
        fetchMachines().catch(() => null),
        fetchRfqs(50).catch(() => null),
        listJobs(50).catch(() => null),
      ]);
      if (cancelled) return;

      const stockItems = stock?.items ?? [];
      const belowMin = stockItems.filter(
        (i) =>
          typeof i.min_qty === "number" && i.min_qty > 0 && i.qty_on_hand < i.min_qty,
      ).length;

      const machineItems = machines?.items ?? [];
      const priced = machineItems.filter(
        (m) => m.status === "active" && m.hourly_rate_try > 0,
      ).length;

      const openRfqs = (rfqs?.items ?? []).filter((r) => !CLOSED.has(r.status));
      const undecidedLines = openRfqs.reduce(
        (sum, r) => sum + Math.max(0, (r.line_count || 0) - (r.decided_count || 0)),
        0,
      );

      const reviewCount = (Array.isArray(jobs) ? jobs : []).filter((j) =>
        isReviewStatus(j.status),
      ).length;

      setAlerts(
        buildAlerts({
          reviewCount,
          belowMin: summary?.stock_critical_count ?? belowMin,
          pricedMachines: priced,
          machineCount: machineItems.length,
          undecidedLines,
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => alerts, [alerts]);
  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
}
